import { axiosInstance } from '@/shared/lib/http/axios';
import { isNonNullable } from '@/shared/utils/is';

import type { AxiosRequestConfig } from 'axios';

// Path-as-key API surface.
//
//   api['/account/login']({ email, password, ... })
//
// Each entry in an endpoint map is built by `endpoint<Req, Res>(method)`,
// which returns a function that POSTs (or GETs) the value at runtime.
// `createApi` composes per-domain endpoint maps into a single typed surface;
// `apps/web_frontend_v3/src/api/index.ts` does the final composition.

type Method = 'GET' | 'POST';

export type Endpoint<Req, Res> = (body?: Req, config?: AxiosRequestConfig) => Promise<Res>;

export type EndpointFactory<Req, Res> = ((path: string) => Endpoint<Req, Res>) & {
  __endpoint: true;
  method: Method;
};

export const endpoint = <Req, Res>(method: Method): EndpointFactory<Req, Res> => {
  const fn = ((path: string): Endpoint<Req, Res> => {
    return (body?: Req, config?: AxiosRequestConfig): Promise<Res> => {
      // The response interceptor unwraps to the payload, so these really do
      // resolve to Res — axios types them as AxiosResponse, hence the cast (the
      // interceptor itself is cast at its own boundary for the same reason).
      // Passing Res as axios' second generic used to express this, but the
      // return type is now a conditional (`R extends AxiosResponseDefault ?
      // AxiosResponse : R`) that TS cannot reduce while R is an unresolved type
      // parameter, so it no longer collapses to Promise<Res>.
      if (method === 'GET') {
        // Annotated rather than inlined: the config carries a `params` generic,
        // and an object literal would infer it as Req, which then conflicts with
        // the caller's plainly-typed config.
        const getConfig: AxiosRequestConfig = { ...config, params: body };
        return axiosInstance.get(path, getConfig) as unknown as Promise<Res>;
      }
      return axiosInstance.post(
        path,
        isNonNullable(body) ? body : undefined,
        config,
      ) as unknown as Promise<Res>;
    };
  }) as EndpointFactory<Req, Res>;
  fn.__endpoint = true;
  fn.method = method;
  return fn;
};

// Use `any` in the constraint to preserve per-key Req/Res inference when
// the caller passes a heterogeneous map literal. The bound surface preserves
// each value's exact `ReturnType<M[K]>`.
export const createApi = <M extends Record<string, EndpointFactory<any, any>>>(
  map: M,
): { [K in keyof M]: ReturnType<M[K]> } => {
  const out: Record<string, unknown> = {};
  for (const path of Object.keys(map)) {
    out[path] = map[path](path);
  }
  return out as { [K in keyof M]: ReturnType<M[K]> };
};
